import { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiContext } from '../../App';
import RichTextEditor from '../common/RichTextEditor';

const NewPositionForm = ({ onClose }) => {
  const API = useContext(ApiContext);
  const navigate = useNavigate();
  
  // State pour les partenaires/entreprises
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null); // Objet complet du partner sélectionné
  const [isCreatingNewCompany, setIsCreatingNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // State pour le dropdown de sélection
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const dropdownRef = useRef(null);

  // State pour les données du formulaire
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    experience: 0,
    skills: '',
    languages: '',
    education: '',
    location: '',
    salary: '',
    contractType: '',
    applicationLink: '',
    status: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Charger la liste des partenaires au montage du composant
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await API.get('/api/partners');
        setPartners(response.data);
        setFilteredPartners(response.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
        setError('Erreur lors du chargement des entreprises');
      }
    };
    fetchPartners();
  }, [API]);

  // Filtrer les partenaires en fonction de la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPartners(partners);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = partners.filter(partner =>
        partner.name.toLowerCase().includes(query) ||
        (partner.city && partner.city.toLowerCase().includes(query))
      );
      setFilteredPartners(filtered);
    }
  }, [searchQuery, partners]);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPartnerDropdown(false);
        setIsCreatingNewCompany(false);
        setSearchQuery('');
      }
    };

    if (showPartnerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPartnerDropdown]);

  // Gérer les changements dans les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gérer la sélection d'un partenaire
  const handleSelectPartner = (partner) => {
    setSelectedPartner(partner);
    setShowPartnerDropdown(false);
    setSearchQuery('');
    setIsCreatingNewCompany(false);
  };

  // Gérer la création d'une nouvelle entreprise depuis le dropdown
  const handleCreateNewCompany = async () => {
    if (!newCompanyName.trim()) {
      setError('Veuillez entrer le nom de l\'entreprise');
      return;
    }

    try {
      const response = await API.post('/api/partners', {
        name: newCompanyName.trim(),
        status: 'prospect'
      });

      // Rafraîchir la liste des partners
      const partnersResponse = await API.get('/api/partners');
      setPartners(partnersResponse.data);
      setFilteredPartners(partnersResponse.data);

      // Sélectionner automatiquement la nouvelle entreprise
      setSelectedPartner(response.data);
      setNewCompanyName('');
      setIsCreatingNewCompany(false);
      setShowPartnerDropdown(false);
      setSearchQuery('');
      setSuccessMessage('Entreprise créée avec succès !');
    } catch (error) {
      console.error('Error creating partner:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création de l\'entreprise');
    }
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // Validation : vérifier qu'on a soit une entreprise sélectionnée, soit un nom de nouvelle entreprise
    if (!selectedPartner) {
      setError('Veuillez sélectionner une entreprise');
      setLoading(false);
      return;
    }

    try {
      // Créer le poste avec l'ID du partenaire sélectionné
      const response = await API.post(
        `/api/positions/partners/${selectedPartner._id}/positions`,
        {
          ...formData,
          skills: formData.skills.split(',').map((skill) => skill.trim()).filter(skill => skill),
          languages: formData.languages.split(',').map((language) => language.trim()).filter(lang => lang)
        }
      );

      setSuccessMessage('Poste créé avec succès !');

      // Réinitialiser le formulaire
      setFormData({
        title: '',
        description: '',
        experience: 0,
        skills: '',
        languages: '',
        education: '',
        location: '',
        salary: '',
        contractType: '',
        applicationLink: '',
        status: ''
      });
      setSelectedPartner(null);

      // Rediriger vers la page de détail du poste après 1.5 secondes
      setTimeout(() => {
        navigate(`/positions/${response.data._id}`);
      }, 1500);

    } catch (error) {
      console.error('Error adding position:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création du poste');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Container de la modal */}
        <div
          className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header du formulaire */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <i className="bi bi-briefcase-fill text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white m-0">Nouveau Poste</h2>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          )}
        </div>
      </div>

          {/* Contenu scrollable */}
          <div className="overflow-y-auto flex-1">
            {/* Messages d'erreur et de succès */}
            <div className="px-6 pt-4">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="bi bi-exclamation-circle text-red-500 mr-2"></i>
                    <span className="text-red-700 text-sm">{error}</span>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <i className="bi bi-check-circle text-green-500 mr-2"></i>
                    <span className="text-green-700 text-sm">{successMessage}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="px-6 pb-6">
        {/* Sélection de l'entreprise */}
        <div className="mb-6 relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="bi bi-building mr-2"></i>
            Entreprise *
          </label>

          {/* Bouton pour ouvrir le dropdown */}
          <button
            type="button"
            onClick={() => {
              setShowPartnerDropdown(!showPartnerDropdown);
              setSearchQuery('');
              setIsCreatingNewCompany(false);
            }}
            className=" px-4 py-2.5 border border-gray-300 rounded-lg hover:border-emerald-500 transition-colors text-left flex items-center justify-between bg-white"
          >
            {selectedPartner ? (
              <div className="flex items-center">
                <i className="bi bi-building text-emerald-600 mr-2"></i>
                <span className="font-medium text-gray-900">{selectedPartner.name}</span>
                {selectedPartner.city && (
                  <span className="text-sm text-gray-500 ml-2">- {selectedPartner.city}</span>
                )}
              </div>
            ) : (
              <span className="text-gray-500">Sélectionner une entreprise</span>
            )}
            <i className={`bi bi-chevron-${showPartnerDropdown ? 'up' : 'down'} text-gray-400 transition-transform ml-2`}></i>
          </button>

          {/* Dropdown menu */}
          {showPartnerDropdown && (
            <div className="absolute z-50  mt-1 bg-white border-1 border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
              {!isCreatingNewCompany ? (
                <>
                  {/* Barre de recherche */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Bouton créer nouvelle entreprise */}
                  <div className="p-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => setIsCreatingNewCompany(true)}
                      className="w-full px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors flex items-center justify-center font-medium text-sm"
                    >
                      <i className="bi bi-plus-circle mr-2"></i>
                      Créer une nouvelle entreprise
                    </button>
                  </div>

                  {/* Liste des entreprises */}
                  <div className="overflow-y-auto">
                    {filteredPartners.length > 0 ? (
                      filteredPartners.map((partner) => (
                        <button
                          key={partner._id}
                          type="button"
                          onClick={() => handleSelectPartner(partner)}
                          className="w-full px-4 py-2.5 hover:bg-emerald-50 transition-colors text-left flex items-center border-b border-gray-100 last:border-b-0"
                        >
                          <i className="bi bi-building text-gray-400 mr-3"></i>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{partner.name}</p>
                            {partner.city && (
                              <p className="text-xs text-gray-500">{partner.city}</p>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        Aucune entreprise trouvée
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Formulaire de création */
                <div className="p-4">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom de l'entreprise
                    </label>
                    <input
                      type="text"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="Ex: Acme Corporation"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreatingNewCompany(false);
                        setNewCompanyName('');
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateNewCompany}
                      className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm flex items-center justify-center"
                    >
                      <i className="bi bi-check-lg mr-1"></i>
                      Créer
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Grille des champs du formulaire */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Titre du poste */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-card-heading mr-2"></i>
              Titre du poste *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Développeur Full Stack"
              required
            />
          </div>

          {/* Localisation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-geo-alt mr-2"></i>
              Localisation
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Paris, Remote"
            />
          </div>

          {/* Type de contrat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-file-earmark-text mr-2"></i>
              Type de contrat
            </label>
            <select
              name="contractType"
              value={formData.contractType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Sélectionnez un type </option>
              <option value="CDI">CDI</option>
              <option value="CDD">CDD</option>
              <option value="Freelance">Freelance</option>
            </select>
          </div>

          {/* Salaire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-currency-euro mr-2"></i>
              Salaire
            </label>
            <input
              type="text"
              name="salary"
              value={formData.salary}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: 45-55k€, 500€/jour"
            />
          </div>

          {/* Expérience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-calendar-check mr-2"></i>
              Expérience (années)
            </label>
            <input
              type="number"
              name="experience"
              value={formData.experience}
              onChange={handleInputChange}
              min="0"
              max="20"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-info-circle mr-2"></i>
              Statut
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Sélectionnez un statut </option>
              <option value="prioritaire">Prioritaire</option>
              <option value="important">Important</option>
              <option value="moyen">Moyen</option>
              <option value="pas urgent">Pas urgent</option>
              <option value="stand by">Stand by</option>
              <option value="recruté">Recruté</option>
            </select>
          </div>

          {/* Formation */}
          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-mortarboard mr-2"></i>
              Formation
            </label>
            <input
              type="text"
              name="education"
              value={formData.education}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Master en informatique, École d'ingénieur"
            />
          </div>

          {/* Compétences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-tools mr-2"></i>
              Compétences (séparées par des virgules)
            </label>
            <input
              type="text"
              name="skills"
              value={formData.skills}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: React, Node.js, MongoDB"
            />
          </div>

          {/* Langues */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-translate mr-2"></i>
              Langues (séparées par des virgules)
            </label>
            <input
              type="text"
              name="languages"
              value={formData.languages}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Ex: Français, Anglais"
            />
          </div>

          {/* Lien de candidature */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="bi bi-link-45deg mr-2"></i>
              Lien de candidature
            </label>
            <input
              type="url"
              name="applicationLink"
              value={formData.applicationLink}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="bi bi-card-text mr-2"></i>
            Description du poste
          </label>
          <div className='border rounded-lg'>
            <RichTextEditor
            value={formData.description}
            onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
            placeholder="Décrivez le poste, les missions, l'environnement de travail..."
            minHeight="250px"
          />
          </div>
          
        </div>

              {/* Boutons d'actions */}
              <div className="flex justify-end space-x-3">
                {onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Création...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle"></i>
                      <span>Créer le poste</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewPositionForm;